<template>
  <slot
    name="trigger"
    :toggle-menu="toggleMenu"
    :open-menu="openMenu"
    :close-menu="closeMenu"
  />

  <HeadlessFloatingWithAnchor
    :anchor-reference="anchorReference"
    :placement="placement"
    :offset="offset"
    :is-visible="isMenuVisible"
  >
    <div
      ref="menuRef"
      class="bg-white app-menu-ignored border border-surface-300 shadow-md rounded-lg min-w-56 max-w-lg flex flex-col py-1 select-none"
    >
      <slot name="items" />
    </div>
  </HeadlessFloatingWithAnchor>
</template>

<script setup lang="ts">
import type { HeadlessFloatingWithAnchorPlacement } from '~/components/Headless/HeadlessFloatingWithAnchor.vue'

defineProps({
  placement: {
    type: String as PropType<HeadlessFloatingWithAnchorPlacement>,
    default: 'bottom',
  },
  offset: {
    type: Number,
    default: 10,
  },
})

// Optional: lets a consumer react to the menu opening/closing (e.g. keep a
// collapsing nav rail open while the menu is up). Additive — ignore to opt out.
const emit = defineEmits<{ visibilityChange: [boolean] }>()

const isMenuVisible = ref(false)
watch(isMenuVisible, (visible) => emit('visibilityChange', visible))
const anchorReference = ref<HTMLElement | null>(null)

function toggleMenu(event: MouseEvent) {
  anchorReference.value = event.currentTarget as HTMLElement
  isMenuVisible.value = !isMenuVisible.value
}

function openMenu(event: MouseEvent) {
  anchorReference.value = event.currentTarget as HTMLElement
  isMenuVisible.value = true
}

function closeMenu() {
  isMenuVisible.value = false
}

const menuRef = useTemplateRef('menuRef')
onClickOutside(
  menuRef,
  () => {
    isMenuVisible.value = false
  },
  {
    ignore: ['.app-menu-ignored', anchorReference],
  },
)

provide('closeMenu', closeMenu)

// Exposed so a consumer can close the menu imperatively — e.g. after a submenu
// item navigates, since nested submenus only close themselves, not this menu.
defineExpose({ openMenu, closeMenu, toggleMenu })

// prefix column sync
const hasPrefixColumn = ref(false)
const setPrefixColumnTrue = () => {
  hasPrefixColumn.value = true
}

provide('hasPrefixColumn', hasPrefixColumn)
provide('setPrefixColumnTrue', setPrefixColumnTrue)
</script>
